// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewStillReviewing.test.tsx
import { type AskPunttMessage } from "@mg/schemas/src/christo/askPuntt";
import { CircleNotch } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

/** The banner over the findings while the turn behind the review still runs. */
export function AskV2ReviewStillReviewing({
  historyMessages,
}: {
  historyMessages: readonly Pick<AskPunttMessage, "checkProgress">[];
}) {
  const { t } = useTranslation("ui");
  // How many of the turn's checks have finished, off the pending message the
  // chat is already carrying (`checkProgress`, written by the warhol progress
  // mirror). Read off `historyMessages` — the server's own messages, not the
  // local thread's — because `checkProgress` is a server field the transport
  // does not carry into the local shape. Absent on a backend that does not
  // publish it yet, and absent before the turn's first leg settles: the banner
  // then simply says less rather than guessing.
  const checksFinished = useMemo(() => {
    for (let index = historyMessages.length - 1; index >= 0; index -= 1) {
      const progress = historyMessages[index].checkProgress;
      if (progress?.length) {
        return progress.filter((check) => check.status === "done").length;
      }
    }
    return 0;
  }, [historyMessages]);

  return (
    <div
      role="status"
      aria-live="polite"
      className="mx-4 mt-3 flex items-start gap-2 rounded-md border border-puntt-neutral-gray-5 bg-puntt-neutral-gray-2 px-3 py-2"
      data-testid="ask-v2-review-still-reviewing"
    >
      <CircleNotch
        size={16}
        weight="bold"
        className="mt-0.5 shrink-0 animate-spin text-puntt-neutral-gray-9"
        aria-hidden="true"
      />
      <div className="min-w-0">
        <Text
          as="p"
          size="2"
          weight="medium"
          data-testid="ask-v2-review-still-reviewing-title"
        >
          {t("askV2.review.stillReviewing")}
        </Text>
        <Text
          as="p"
          size="1"
          color="gray"
          data-testid="ask-v2-review-still-reviewing-hint"
        >
          {t("askV2.review.stillReviewingHint")}
          {checksFinished > 0
            ? ` ${t("askV2.review.stillReviewingChecks", {
                done: checksFinished,
              })}`
            : ""}
        </Text>
      </div>
    </div>
  );
}
